export const AI_IMAGE_PROMPT_MAX_LENGTH = 5000
export const AI_IMAGE_UPLOAD_MAX_MB = 50

export const AI_IMAGE_MODELS = [
  {
    id: 'nano-banana-2',
    generationType: 'nano-banana-2',
    name: 'Nano Banana 2',
    tagline: 'High quality image generation with wide aspect support',
    supportsImageInput: true,
    maxImages: 14,
    aspectRatios: [
      'auto',
      '1:1',
      '2:3',
      '3:2',
      '3:4',
      '4:3',
      '4:5',
      '5:4',
      '9:16',
      '16:9',
      '1:4',
      '4:1',
      '1:8',
      '8:1',
      '21:9',
    ],
    resolutions: ['1K', '2K', '4K'],
    defaultResolution: '1K',
    resolutionCredits: {
      '1K': 2,
      '2K': 3,
      '4K': 4,
    },
  },
  {
    id: 'nano-banana-2-lite',
    generationType: 'nano-banana-2-lite',
    name: 'Nano Banana 2 Lite',
    tagline: 'Lightweight image generation for quick drafts',
    supportsImageInput: true,
    maxImages: 16,
    aspectRatios: ['auto', '1:1', '2:3', '3:2', '9:16', '16:9', '4:3', '3:4', '21:9'],
    fixedCredits: 1,
  },
  {
    id: 'google-image-4',
    generationType: 'google-image-4',
    name: 'Google Imagen-4',
    tagline: 'High-quality text-to-image generation',
    supportsImageInput: false,
    aspectRatios: ['1:1', '16:9', '9:16', '3:4', '4:3'],
    modes: ['fast', 'standard', 'ultra'],
    defaultMode: 'standard',
    modeCredits: {
      fast: 1,
      standard: 2,
      ultra: 3,
    },
  },
  {
    id: 'wan-2.7-image-pro',
    generationType: 'wan-2.7-image-pro',
    name: 'Wan 2.7 Image Pro',
    tagline: 'Premium image generation with up to 4 samples',
    supportsImageInput: true,
    maxImages: 9,
    aspectRatios: ['auto', '16:9', '9:16', '4:3', '3:4', '1:1'],
    resolutions: ['1K', '2K', '4K'],
    defaultResolution: '1K',
    supportsSamples: true,
    minSamples: 1,
    maxSamples: 4,
    creditPerSample: 3,
  },
  {
    id: 'wan-2.7-image',
    generationType: 'wan-2.7-image',
    name: 'Wan 2.7 Image',
    tagline: 'Fast image generation with reference image support',
    supportsImageInput: true,
    maxImages: 9,
    aspectRatios: ['auto', '16:9', '9:16', '4:3', '3:4', '1:1'],
    resolutions: ['1K', '2K'],
    defaultResolution: '1K',
    supportsSamples: true,
    minSamples: 1,
    maxSamples: 4,
    creditPerSample: 1,
  },
  {
    id: 'gpt-image-2',
    generationType: 'gpt-image-2',
    name: 'GPT Image 2',
    tagline: 'Precise prompt following and flexible sizes',
    supportsImageInput: true,
    maxImages: 16,
    aspectRatios: ['auto', '1:1', '2:3', '3:2', '9:16', '16:9', '4:3', '3:4', '21:9'],
    resolutions: ['1K', '2K', '4K'],
    defaultResolution: '1K',
    resolutionCredits: {
      '1K': 1,
      '2K': 2,
      '4K': 3,
    },
  },
  {
    id: 'grok-imagine',
    generationType: 'grok-imagine',
    name: 'Grok Imagine',
    tagline: 'Creative image generation and remixing',
    supportsImageInput: true,
    maxImages: 5,
    aspectRatios: ['1:1', '2:3', '3:2', '16:9', '9:16'],
    fixedCredits: 1,
  },
]

export const DEFAULT_AI_IMAGE_MODEL_ID = AI_IMAGE_MODELS[0].id

export function aiImageModelById(modelId) {
  return AI_IMAGE_MODELS.find((model) => model.id === modelId) ?? AI_IMAGE_MODELS[0]
}

export function modelIdFromAiImageGenerationType(generationType) {
  const value = String(generationType || '').trim().toLowerCase()
  if (!value) return null
  return AI_IMAGE_MODELS.find((model) => model.generationType.toLowerCase() === value)?.id ?? null
}

export function createDefaultAiImageSettings(modelId = DEFAULT_AI_IMAGE_MODEL_ID) {
  const model = aiImageModelById(modelId)
  return {
    aspectRatio: model.aspectRatios[0] ?? '1:1',
    mode: model.defaultMode ?? model.modes?.[0] ?? '',
    resolution: model.defaultResolution ?? model.resolutions?.[0] ?? '',
    samples: model.minSamples ?? 1,
  }
}

export function normalizeAiImageSettings(settings = {}, modelId = DEFAULT_AI_IMAGE_MODEL_ID) {
  const model = aiImageModelById(modelId)
  const defaults = createDefaultAiImageSettings(modelId)
  const next = {
    ...defaults,
    ...settings,
  }

  if (!model.aspectRatios.includes(next.aspectRatio)) {
    next.aspectRatio = defaults.aspectRatio
  }

  if (model.modes?.length) {
    if (!model.modes.includes(next.mode)) next.mode = defaults.mode
  } else {
    next.mode = ''
  }

  if (model.resolutions?.length) {
    if (!model.resolutions.includes(next.resolution)) next.resolution = defaults.resolution
  } else {
    next.resolution = ''
  }

  if (model.supportsSamples) {
    const min = model.minSamples ?? 1
    const max = model.maxSamples ?? 1
    const raw = Math.round(Number(next.samples))
    next.samples = Number.isFinite(raw) ? Math.min(max, Math.max(min, raw)) : min
  } else {
    next.samples = 1
  }

  return next
}

export function estimateAiImageCredits(modelId, settings = {}) {
  const model = aiImageModelById(modelId)
  const normalized = normalizeAiImageSettings(settings, modelId)

  if (model.modeCredits) {
    return model.modeCredits[normalized.mode] ?? 0
  }

  if (model.supportsSamples) {
    return (model.creditPerSample ?? 0) * normalized.samples
  }

  if (model.resolutionCredits) {
    return model.resolutionCredits[normalized.resolution] ?? 0
  }

  return model.fixedCredits ?? 0
}
